import type { ParamGroup, ParamSpec } from "../components/data/ParamForm";
// 显式带 .ts —— node 的 ESM 解析不做扩展名补全，不写就跑不了单元测试（vite 两种都认）
import { fmt } from "./model.ts";
import type { MsgKey } from "./i18n.tsx";
import type { Money } from "./big.ts";

const n = (v: number | null) => (v == null || Number.isNaN(v) ? 0 : v);

/** 骰子参数。字段与默认值来自需求脑图「全局配置 · 骰子参数」 */
export interface DiceParams extends Record<string, number | string | null> {
  /** incomeAnchor（首关收入 B）是金币量级，没有上限：Money，服务端读出是十进制字符串 */
  unifiedB: number | null;
  E: number | null; rho: number | null; incomeK: number | null; incomeAnchor: Money | null; targetBase: number | null; targetGrowth: number | null;
  cap: number | null; capStep: number | null; capStarStep: number | null; regen: number | null;
  /*
   * 下面三项以前页面上没有入口，只在库里躺着。发布闸却把它们当必填
   * （新建版本是 -1 占位），于是新代理**在界面上怎么填都发不出去**——
   * 清单里报 common.diceInit 没填，而配置台根本没有这一格。
   */
  pDouble: number | null; capMax: number | null; init: number | null;
  /** 副玩法由客户端选类型（cfg_main_common.subgame_client_pick）：0 关 / 1 开。单人掷骰才有意义 */
  subgamePick: number | null;
  /**
   * 穿越概率（%，cfg_main_common.warp_prob_bps ÷ 100；2026-10-05）。不在骰子参数页显示——
   * 入口在 spin 奖励表格下面（产品定的位置），值跟着 common 一起存
   */
  warpProb: number | null;
  /** 救助金（cfg_main_common.relief_*，2026-10-26）：每次几颗 / 每天几次 / 骰子不多于几颗才能领。次数或颗数 0 = 不开 */
  reliefQty: number | null; reliefTimes: number | null; reliefThreshold: number | null;
  /** 挂机收益（cfg_main_common.idle_tap_*，2026-10-27）：每下 = 每投金币的千分之几 [下限, 上限]（10-28 起区间，客户端随机）/ 一次动画最多算几下。下限或次数 0 = 不开 */
  idlePct: number | null; idleMax: number | null; idleCap: number | null;
}
export const DEFAULT_DICE: DiceParams = {
  unifiedB: 0, E: 3.1, rho: 0.9, incomeK: null, incomeAnchor: null, targetBase: null, targetGrowth: null, cap: 50, capStep: 0, capStarStep: 0, regen: 4, pDouble: 0.15, capMax: 999, init: 30, subgamePick: 0, warpProb: 0, reliefQty: 0, reliefTimes: 0, reliefThreshold: 0, idlePct: 0, idleMax: 0, idleCap: 0,
};

/** 造价与收入同页配置；仍写 common，字段精度与保存规则保持不变。 */
const COST_FIELDS: ParamSpec<DiceParams>[] = [
  { key: "E", label: "p.E", hint: "p.E.hint", video: "e" },
  { key: "rho", label: "p.rho", hint: "p.rho.hint", video: "rho" },
  { key: "targetBase", label: "p.targetBase", hint: "p.targetBase.hint", note: "p.targetBase.note", video: "rolls",
    valid: (v) => v == null || (Number.isInteger(v) && v > 0 && v <= 2147483647) },
  { key: "targetGrowth", video: "rolls", label: "p.targetGrowth", hint: "p.targetGrowth.hint", note: "p.targetGrowth.note",
    valid: (v) => v == null || (Number.isFinite(v) && v > 0 && v * 10000 <= 2147483647 && Math.abs(v * 10000 - Math.round(v * 10000)) < 1e-7) },
  // 金币量级，income_anchor_b 已改 DECIMAL(65,0)：不再限 INT32（leo 2026-10-16）。留空 = 用第 1 关的 B
  { key: "incomeAnchor", video: "k", label: "p.incomeAnchor", hint: "p.incomeAnchor.hint", note: "p.incomeAnchor.note",
    exact: true, validExact: (v) => v == null || v > 0n },
  { key: "incomeK", label: "p.incomeK", hint: "p.incomeK.hint", note: "cost.params.kNote", video: "k",
    valid: (v) => v == null || (Number.isFinite(v) && v > 0 && v * 10000 <= 2147483647 && Math.abs(v * 10000 - Math.round(v * 10000)) < 1e-7) },
];

export const COST_GROUPS: ParamGroup<DiceParams>[] = [
  { title: "cost.params.algorithm", fields: COST_FIELDS.slice(0, 2) },
  { title: "cost.params.rolls", fields: COST_FIELDS.slice(2, 4) },
  { title: "cost.params.income", fields: COST_FIELDS.slice(4, 6) },
];

/**
 * 造价页的参数组。model 2（单掷金币）版本：金币与造价都按每关 / 每星的 g 算（造价 = 目标骰次 × g × ρ），
 * E 与收入曲线（首关 B、K）不参与任何计算，只在回到旧口径时用——这里不显示，免得运营以为改了有用；值原样保留
 */
export function costGroups(buildingSystem: string, unifiedB = false, economyModel: 1 | 2 = 1, rollsCurveOn = true): ParamGroup<DiceParams>[] {
  // 关卡制的「算法起始骰次 / 骰次增长」整条曲线会覆盖逐关目标骰次，没开的代理不显示，免得被当成要先打开的开关（leo 2026-10-27）；
  // 已经开着的照常显示，留着关掉的入口。萌宠小镇的星级骰次曲线也用这两个字段，不动
  if (buildingSystem !== "pettown" && !rollsCurveOn) {
    return costGroups(buildingSystem, unifiedB, economyModel)
      .map((g) => ({ ...g, fields: g.fields.filter((f) => f.key !== "targetBase" && f.key !== "targetGrowth") }))
      .filter((g) => g.fields.length > 0);
  }
  if (economyModel === 2) {
    return costGroups(buildingSystem, false, 1)
      .filter((g) => g.title !== "cost.params.income")
      .map((g) => ({ ...g, fields: g.fields.filter((f) => f.key !== "E").map((f) => (f.key === "rho" ? { ...f, hint: "p.rho.hintG" as MsgKey, video: undefined } : f)) }));
  }
  const groups: ParamGroup<DiceParams>[] = buildingSystem === "pettown" ? COST_GROUPS.map(g => ({ ...g, fields: g.fields.map((f): ParamSpec<DiceParams> => ({ ...f, video:undefined, ...(f.key === "targetBase" ? {label:"pt.econ.rollBase", hint:"pt.econ.rollBaseHint",note:"pt.econ.rollBaseNote"} : f.key === "targetGrowth" ? {label:"pt.econ.rollGrowth", hint:"pt.econ.rollGrowthHint"} : f.key === "incomeK" ? {hint:"pt.econ.legacyKHint"} : {}) })) })) : COST_GROUPS;
  if (!unifiedB) return groups;
  return groups.map(g => ({
    ...g,
    title: g.title === "cost.params.income" ? "cost.unifiedB.group" : g.title,
    fields: g.fields.map(original => {
      const f = { ...original, video:undefined };
      if (f.key === "E") return { ...f, hint: "p.unifiedE.hint" };
      if (f.key === "rho") return { ...f, hint: "p.unifiedRho.hint" };
      if (f.key === "targetGrowth") return { ...f, hint: buildingSystem === "pettown" ? "pt.econ.rollGrowthHint" : "p.unifiedRollGrowth.hint" };
      if (f.key === "incomeAnchor") return { ...f, label: "p.unifiedAnchor", hint: buildingSystem === "pettown" ? "pt.econ.anchorHint" : "p.unifiedAnchor.hint", note: buildingSystem === "pettown" ? "pt.econ.anchorNote" : "p.unifiedAnchor.note" };
      if (f.key === "incomeK") return { ...f, label: "p.unifiedK", hint: buildingSystem === "pettown" ? "pt.econ.kHint" : "p.unifiedK.hint", note: buildingSystem === "pettown" ? "pt.econ.kNote" : "p.unifiedK.note" };
      return f;
    }),
  }));
}

export const DICE_GROUPS: ParamGroup<DiceParams>[] = [
  {
    title: "p.grp.dice",
    footer: "p.foot.dice",
    fields: [
      {
        key: "cap", label: "p.cap", unit: "u.count", hint: "p.cap.hint",
        note: (d, t) => (n(d.regen) > 0 ? t("p.cap.note", { h: ((n(d.cap) * n(d.regen)) / 60).toFixed(1) }) : ""),
      },
      {
        key: "capStep", label: "p.capStep", unit: "u.countPerLevel", hint: "p.capStep.hint",
        valid: (v) => v != null && v >= 0,
        note: (d, t) =>
          n(d.capStep) > 0 ? t("p.capStep.note", { v: fmt(n(d.cap) + n(d.capStep) * 49) }) : t("p.capStep.zero"),
      },
      {
        key: "regen", label: "p.regen", unit: "u.minPerCount", hint: "p.regen.hint",
        note: (d, t) => (n(d.regen) > 0 ? t("p.regen.note", { n: fmt(Math.floor((24 * 60) / n(d.regen))) }) : ""),
      },
    ],
  },
];

/** 对抗与控骰。默认值来自需求脑图「全局配置 · 对抗与控骰」 */
export interface FightParams extends Record<string, number | null> {
  /* 这四项同样是「库里有、闸门要、页面没有入口」的，补进来（见 DiceParams 的注释） */
  cardLimit: number | null; puzzleLimit: number | null; adLimit: number | null;
  stealPct: number | null; stealCap: number | null; stealFloor: number | null; wardCap: number | null;
  atkSuccess: number | null; atkBlock: number | null;
  // 雷神掉钱三件套（定案④「掉钱不掉星」）。比例或上限为 0 = 本版不开掉钱
  thunderPct: number | null; thunderCap: number | null; thunderFloor: number | null;
  clawbackDays: number | null; clawbackBonus: number | null; repairPct: number | null;
  visitCap: number | null; matchRange: number | null;
  protectNpc: number | null; protectHalf: number | null;
  giftCap: number | null; askCap: number | null;
  /** 单人倍率基础上限（stake_tier_base，《建造天赋树 V5.5》）：不领天赋节点能押的最高倍率，上限制 */
  stakeTierBase: number | null;
  // 社交 / 个人中心的四个上限（0 = 关掉那条路）
  friendLimit: number | null; socialEventCap: number | null;
  avatarUploadCap: number | null; uidQueryCap: number | null;
  /** 拼图提示每日上限（puzzle_hint_daily_limit，2026-10-22）：每名玩家每天几次，0 = 不给提示 */
  puzzleHintCap: number | null;
}
export const DEFAULT_FIGHT: FightParams = {
  stealPct: 0.25, stealCap: 30, stealFloor: 2, wardCap: 3,
  atkSuccess: 0.6, atkBlock: 8,
  // 产品明确「不定量，管端配」，这里不编数：null = 界面显示空输入框，
  // 与迁移的「默认关闭」一致。编个 10% 会让运营以为那是定过的值
  thunderPct: null, thunderCap: null, thunderFloor: null,
  clawbackDays: 14, clawbackBonus: 0.5, repairPct: 0.3,
  visitCap: 8, matchRange: 2,
  protectNpc: 3, protectHalf: 6,
  giftCap: 5, askCap: 5, stakeTierBase: 1,
  friendLimit: 500, socialEventCap: 50, avatarUploadCap: 10, uidQueryCap: 200, puzzleHintCap: 3,
  cardLimit: 20, puzzleLimit: 20, adLimit: 5,
};

/**
 * 对抗参数的归属映射（设计 globalpage.jsx 的 FIGHT_BY_TILE）：
 * 存储仍是全局参数一份，编辑入口下放到各副玩法格子页；'global' 留在对抗与控骰页。
 */
export const FIGHT_OWNER: Record<string, keyof FightParams | never> = {};
/** 这一组是补上来的：它们本来就在库里、发布闸也要，只是先前没给编辑入口 */
export const DICE_MORE_GROUP: ParamGroup<DiceParams> = {
  title: "p.grp.diceMore",
  fields: [
    // 与 ρ 同款：库里存 bps，页面编 0~1 的小数
    { key: "pDouble", label: "p.pDouble", hint: "p.pDouble.hint" },
    { key: "capMax", label: "p.capMax", unit: "u.count", hint: "p.capMax.hint" },
    { key: "init", label: "p.diceInit", unit: "u.count", hint: "p.diceInit.hint" },
  ],
};
DICE_GROUPS.push(DICE_MORE_GROUP);

/** 副玩法由客户端选类型（2026-09-30 定）：按代理开，开了之后抽中副玩法由客户端挑哪一种、POST /v1/game/subgame 领 */
export const DICE_SUBGAME_GROUP: ParamGroup<DiceParams> = {
  title: "p.grp.subgamePick",
  fields: [
    // 只有开 / 关两种：做成勾选框，不让人猜数字框里该填 0 还是 1（10-10 从零建代理实测卡在这儿）
    { key: "subgamePick", label: "p.subgamePick", hint: "p.subgamePick.hint", type: "bool" },
  ],
};
DICE_GROUPS.push(DICE_SUBGAME_GROUP);

/** 救助金（2026-10-26）：骰子用完（不多于阈值）时玩家每天能领几次、每次几颗。客户端调 GET /v1/game/relief、POST /v1/game/relief/claim */
export const DICE_RELIEF_GROUP: ParamGroup<DiceParams> = {
  title: "p.grp.relief",
  fields: [
    { key: "reliefQty", label: "p.reliefQty", unit: "u.count", hint: "p.reliefQty.hint", valid: (v) => v != null && Number.isInteger(v) && v >= 0 },
    { key: "reliefTimes", label: "p.reliefTimes", unit: "u.times", hint: "p.reliefTimes.hint", valid: (v) => v != null && Number.isInteger(v) && v >= 0 && v <= 999 },
    { key: "reliefThreshold", label: "p.reliefThreshold", unit: "u.count", hint: "p.reliefThreshold.hint", valid: (v) => v != null && Number.isInteger(v) && v >= 0 },
  ],
};
DICE_GROUPS.push(DICE_RELIEF_GROUP);

/**
 * 挂机收益（2026-10-27，「通关配置 → 挂机收益」页，不在骰子参数页）：骰子动画期间点屏幕，每下给本关每投金币的千分之几，
 * 10-28 起是区间 [下限, 上限]，客户端每下随机。存的是万分比，页面按千分比填、最多一位小数。客户端调 GET /v1/game/idle、POST /v1/game/idle/claim
 */
const permille = (v: number | null) => v != null && v >= 0 && v <= 1000 && Math.abs(v * 10 - Math.round(v * 10)) < 1e-9;
export const IDLE_GROUP: ParamGroup<DiceParams> = {
  title: "idle.grp",
  fields: [
    { key: "idlePct", label: "idle.pct", unit: "u.permille", hint: "idle.pct.hint", valid: permille },
    { key: "idleMax", label: "idle.max", unit: "u.permille", hint: "idle.max.hint", valid: permille },
    { key: "idleCap", label: "idle.cap", unit: "u.times", hint: "idle.cap.hint", valid: (v) => v != null && Number.isInteger(v) && v >= 0 && v <= 10000 },
  ],
};

/** 同上：库里有、发布闸要，先前页面上没有入口的那几项 */
export const FIGHT_MORE_GROUP: ParamGroup<FightParams> = {
  title: "p.grp.fightMore",
  fields: [
    { key: "cardLimit", label: "p.cardLimit", unit: "u.times", hint: "p.cardLimit.hint" },
    { key: "puzzleLimit", label: "p.puzzleLimit", unit: "u.times", hint: "p.puzzleLimit.hint" },
    { key: "adLimit", label: "p.adLimit", unit: "u.times", hint: "p.adLimit.hint" },
  ],
};

export const FIGHT_TILE_FIELDS: Record<string, Array<{ key: keyof FightParams & string; label: MsgKey; unit: MsgKey; hint: MsgKey; pct?: boolean }>> = {
  thunder: [
    { key: "atkSuccess", label: "p.atkSuccess", unit: "u.pct", hint: "p.atkSuccess.hint", pct: true },
    { key: "atkBlock", label: "p.atkBlock", unit: "u.B", hint: "p.atkBlock.hint" },
    { key: "repairPct", label: "p.repairPct", unit: "u.pct", hint: "p.repairPct.hint", pct: true },
    // 掉钱三件套（定案④「掉钱不掉星」）。与贫乏神那三个同形状但独立配置，
    // 比例或上限填 0 = 本版雷神只劈楼不掉钱
    { key: "thunderPct", label: "p.thunderPct", unit: "u.pct", hint: "p.thunderPct.hint", pct: true },
    { key: "thunderCap", label: "p.thunderCap", unit: "u.B", hint: "p.thunderCap.hint" },
    { key: "thunderFloor", label: "p.thunderFloor", unit: "u.B", hint: "p.thunderFloor.hint" },
  ],
  visit: [
    { key: "stealPct", label: "p.stealPct", unit: "u.pct", hint: "p.stealPct.hint", pct: true },
    { key: "stealCap", label: "p.stealCap", unit: "u.B", hint: "p.stealCap.hint" },
    { key: "stealFloor", label: "p.stealFloor", unit: "u.B", hint: "p.stealFloor.hint" },
    { key: "visitCap", label: "p.visitCap", unit: "u.times", hint: "p.visitCap.hint" },
    // 福神讨回只针对被偷（抢金库）的记录，跟偷取几项放一起（leo 2026-10-21）
    { key: "clawbackDays", label: "p.clawbackDays", unit: "u.day", hint: "p.clawbackDays.hint" },
    { key: "clawbackBonus", label: "p.clawbackBonus", unit: "u.pct", hint: "p.clawbackBonus.hint", pct: true },
  ],
  omamori: [
    { key: "wardCap", label: "p.wardCap", unit: "u.count", hint: "p.wardCap.hint" },
  ],
};

export const FIGHT_GROUPS: ParamGroup<FightParams>[] = [
  {
    // 设计稿是一整块八个字段，不分小组：这八项互相牵制（偷取比例配高了要靠御守上限压回来），
    // 拆成三张卡反而看不出它们是一套。
    // 攻击/偷取/御守的数值编辑入口已下放到各副玩法格子页（FIGHT_TILE_FIELDS 映射），
    // 本页只保留跨玩法共用的全局项（设计 globalpage.jsx 定版）
    title: "p.grp.fight",
    notice: { tone: "info", text: "p.fight.moved" },
    fields: [
      { key: "matchRange", label: "p.matchRange", unit: "u.plusMinusLevel", hint: "p.matchRange.hint", note: (d, t) => t("p.matchRange.note", { n: n(d.matchRange) }) },
      // 卡牌社交上限（leo 已定：动态配置，0 = 无封顶）
      { key: "giftCap", label: "p.giftCap", unit: "u.times", hint: "p.giftCap.hint" },
      // 单人倍率基础上限（设计 globalpage.jsx FIGHT_FIELDS stakeTierBase，2026-10-16）
      { key: "stakeTierBase", label: "p.stakeTierBase", unit: "talentx.unit.x", hint: "p.stakeTierBase.hint" },
      // 社交 / 个人中心（2026-09-03）。四项都是 0 = 关掉那条路，不是「不限」
      { key: "friendLimit", label: "p.friendLimit", unit: "u.person", hint: "p.friendLimit.hint" },
      { key: "socialEventCap", label: "p.socialEventCap", unit: "u.times", hint: "p.socialEventCap.hint" },
      { key: "avatarUploadCap", label: "p.avatarUploadCap", unit: "u.times", hint: "p.avatarUploadCap.hint" },
      { key: "uidQueryCap", label: "p.uidQueryCap", unit: "u.times", hint: "p.uidQueryCap.hint" },
      // 拼图提示每日上限（2026-10-22 host）：所有 idol / 拼图共用，0 = 不给提示
      { key: "puzzleHintCap", label: "p.puzzleHintCap", unit: "u.times", hint: "p.puzzleHintCap.hint" },
      { key: "askCap", label: "p.askCap", unit: "u.times", hint: "p.askCap.hint" },
    ],
  },
  {
    // 新手保护从写死说明改成可编辑（leo 2026-08-21 拍板做映射时一并放开）
    title: "p.grp.newbie",
    fields: [
      { key: "protectNpc", label: "p.protectNpc", unit: "u.level", hint: "p.protectNpc.hint" },
      { key: "protectHalf", label: "p.protectHalf", unit: "u.level", hint: "p.protectHalf.hint" },
    ],
  },
];
FIGHT_GROUPS.push(FIGHT_MORE_GROUP);


/*
 * 竞拍参数已移出：竞拍改成按关卡分期（见 lib/auction.ts + pages/AuctionPage.tsx），
 * 不再是一组全局数字。
 */

/* ── 萌宠小镇（pettown）：全局参数 ──
 * 只在体系 = pettown 时生效。星级表 / 槽位表在各自的页面配，这里只有四个全局项。 */

export interface PettownParams extends Record<string, number | null> {
  /** 累计星数上限。星级表按它出行；解锁星数超过它的槽位永远开不出来 */
  maxStar: number | null;
  /** 几星一级：玩家等级 = ⌊已拿到的星数 ÷ 本值⌋ + 1。一颗星就是一格（leo 2026-09-17），格数不再配 */
  starsPerLevel: number | null;
  /** 修复费比例（页面按百分比编，落库 bps）。按当前这颗星的总价算 */
  repairPct: number | null;
  /** 破坏者收益比例。攻方拿受害者当前星总价 × 它，由系统出，受害者不掉金币 */
  attackerPct: number | null;
  /** 新手保护（星）：星数 ≤ 它只遇 NPC、也不会被真人打。萌宠不看对抗页的保护关数 */
  protectStar: number | null;
  /** 真人匹配带（± 星）。0 = 不限星数 */
  matchRangeStars: number | null;
}

/**
 * 宠物升星的五个全局参数（`cfg_main_common` 的五列）。
 *
 * 三个开关按 tinyint 存，所以这里也是 `number`（0 / 1）——表单用 `type: "bool"` 编成勾选框。
 *
 * ⚠️ **当前全部不生效**：升星是 V2，玩家的宠物恒为 1★，够不到上限、触发不了满级。
 */
export interface PetStarParams extends Record<string, number | string | null> {
  /** 星级上限（满级线）。到顶后再领到同一只会改发金币 */
  starMax: number | null;
  /** 抽到已满级的角色时改发的金币数。金币量级没有上限：Money */
  maxedCoin: Money | null;
  /** 替代金币吃不吃骰子倍数。1 = 吃 */
  maxedApplyMultiplier: number | null;
  /** 替代金币算不算一次完整的领养结算（重置保底、进冷却）。1 = 算 */
  maxedCountsAsAdopt: number | null;
}

export const DEFAULT_PET_STAR: PetStarParams = {
  starMax: 10, maxedCoin: 0, maxedApplyMultiplier: 1, maxedCountsAsAdopt: 1,
};

/** 允许 0（替代金币可以配 0 = 不发钱，只走一次领养结算） */
const NON_NEGATIVE = (v: number | null) => v != null && v >= 0;

export const PET_STAR_GROUPS: ParamGroup<PetStarParams>[] = [
  {
    title: "petStar.grp.global",
    notice: { tone: "warning", text: "petStar.notice.v1" },
    fields: [
      { key: "starMax", label: "petStar.max", unit: "u.star", hint: "petStar.max.hint" },
      { key: "maxedCoin", label: "petStar.maxedCoin", hint: "petStar.maxedCoin.hint", exact: true },
      { key: "maxedApplyMultiplier", label: "petStar.maxedMultiplier", hint: "petStar.maxedMultiplier.hint", type: "bool" },
      { key: "maxedCountsAsAdopt", label: "petStar.maxedCountsAsAdopt", hint: "petStar.maxedCountsAsAdopt.hint", type: "bool" },
    ],
  },
];

/**
 * idol 亲密度的全局参数（`cfg_main_common` 的一列）。等级曲线与来源是两张表，不在这里。
 * 与升星页同一个口径：进同一份快照、由顶栏统一保存，不单开保存按钮。
 */
export interface IntimacyParams extends Record<string, number | null> {
  /** 单个 idol 每日获取上限，所有来源加总后封顶。0 = 不限 */
  idolDailyCap: number | null;
}

/** 规则稿 2.4：单日单 idol 上限 100 点 */
export const DEFAULT_INTIMACY: IntimacyParams = { idolDailyCap: 100 };

/**
 * 神灯的一个全局参数（cfg_main_common.lamp_pity_threshold）：万能卡保底阈值 N。
 * 《Fast Ludo 棋盘格位表 v4.2》规则 12：连续 N 次神灯没出万能卡第 N 次必出，默认 10，觉得慢压到 6–8，纯数值、不用提版本。
 * 挂在神灯掉落表页上、进同一份快照、由顶栏统一保存。
 */
export interface LampParams extends Record<string, number | null> {
  /** 0 = 关闭保底；上限 1000（与服务端 LAMP_MAX_THRESHOLD 同步） */
  pityThreshold: number | null;
}
export const DEFAULT_LAMP: LampParams = { pityThreshold: 10 };
export const LAMP_MAX_THRESHOLD = 1000;

export const INTIMACY_GROUPS: ParamGroup<IntimacyParams>[] = [
  {
    title: "intimacy.grp.daily",
    fields: [
      { key: "idolDailyCap", label: "intimacy.idolDailyCap", unit: "intimacy.u.point", hint: "intimacy.idolDailyCap.hint", valid: NON_NEGATIVE },
    ],
  },
];

export const DEFAULT_PETTOWN: PettownParams = {
  maxStar: 1000, starsPerLevel: 8, repairPct: 0.3, attackerPct: 0.3, protectStar: 0, matchRangeStars: 0,
};

export const PETTOWN_GROUPS: ParamGroup<PettownParams>[] = [
  {
    title: "pt.grp.main",
    notice: { tone: "info", text: "pt.notice.units" },
    fields: [
      { key: "maxStar", label: "pt.maxStar", unit: "u.star", hint: "pt.maxStar.hint",
        note: (d, t) => t("pt.maxStar.note", { n: fmt(n(d.maxStar)) }) },
      { key: "starsPerLevel", label: "pt.starsPerLevel", unit: "u.star", hint: "pt.starsPerLevel.hint",
        valid: (v) => v != null && Number.isInteger(v) && v >= 1,
        note: (d, t) => (n(d.starsPerLevel) >= 1
          ? t("pt.starsPerLevel.note", { s: fmt(n(d.maxStar)), l: fmt(Math.floor(n(d.maxStar) / n(d.starsPerLevel)) + 1) })
          : "") },
    ],
  },
  {
    title: "pt.grp.damage",
    footer: "pt.foot.damage",
    fields: [
      { key: "repairPct", label: "pt.repairPct", unit: "u.pct", hint: "pt.repairPct.hint" },
      { key: "attackerPct", label: "pt.attackerPct", unit: "u.pct", hint: "pt.attackerPct.hint" },
    ],
  },
  {
    // 萌宠按星数判保护期与匹配带（对抗页那两项按关，萌宠下不生效）
    title: "pt.grp.match",
    fields: [
      { key: "protectStar", label: "pt.protectStar", unit: "u.star", hint: "pt.protectStar.hint" },
      { key: "matchRangeStars", label: "pt.matchRangeStars", unit: "u.plusMinusStar", hint: "pt.matchRangeStars.hint",
        note: (d, t) => (n(d.matchRangeStars) > 0
          ? t("pt.matchRangeStars.note", { n: fmt(n(d.matchRangeStars)) })
          : t("pt.matchRangeStars.none")) },
    ],
  },
];


/** 小镇按星配置恢复上限，关卡增量另存，切换体系不会把原配置换单位。 */
export function diceGroups(buildingSystem: string): ParamGroup<DiceParams>[] {
  if (buildingSystem !== "pettown") return DICE_GROUPS;
  /*
   * 萌宠小镇不自动恢复骰子（2026-09-14）：体力只从副建筑「体力树」领取。恢复间隔、每级 / 每星增量、上限封顶
   * 都不生效，不显示（发布闸也不再要求）；基础上限改叫「体力上限（基础值）」——收体力树最多收到它，天赋「体力上限」在它上面加
   */
  const hidden = new Set<string>(["regen", "capStep", "capStarStep", "capMax"]);
  return DICE_GROUPS.map((g) => ({ ...g, fields: g.fields.filter((f) => !hidden.has(String(f.key))).map((f): ParamSpec<DiceParams> => f.key !== "cap" ? f : {
    ...f, label: "p.capBase", hint: "p.capBase.hint", note: (_d, t) => t("p.capBase.note"),
  }) })).filter((g) => g.fields.length > 0);
}


/** 小镇范围/保护在小镇参数页配置 */
export function fightGroups(buildingSystem: string): ParamGroup<FightParams>[] {
  if (buildingSystem !== "pettown") return FIGHT_GROUPS;
  return FIGHT_GROUPS.map((g) => ({ ...g, fields: g.fields.filter((f) => !["matchRange","protectNpc"].includes(String(f.key))) })).filter(g=>g.fields.length);
}
